import { scanPromotionalWords } from '../TemplateDeliverabilityChecker';

// Live, word/sentence-level spam analysis for the composer. This is the free,
// instant engine (no AI) — it focuses on wording and phrasing, NOT structural
// things like link/image counts, which belong to the template deliverability
// check. Runs on every keystroke, so it must stay cheap and synchronous.

const FAKE_REPLY_RE = /^\s*(re|fw|fwd|aw|wg)\s*:/i;
const PUNCT_RE = /!{2,}|\?{2,}|!\?|\?!/;
const PHISHING_RE = /\b(verify\s+your\s+account|confirm\s+your\s+(account|password|payment|identity)|update\s+your\s+(payment|billing|account)|konto\s+(verifizieren|best[aä]tigen)|passwort\s+(best[aä]tigen|zur[uü]cksetzen)|suspicious\s+activity|unusual\s+activity|account\s+(suspended|locked)|zahlung\s+best[aä]tigen)\b/i;

const stripHtml = (s) => (s || '')
  .replace(/<[^>]*>/g, ' ')
  .replace(/&nbsp;/gi, ' ')
  .replace(/&[a-z0-9#]+;/gi, ' ');

const countCapsWords = (text) => (text || '').split(/\s+/).filter((w) => {
  const letters = w.replace(/[^A-Za-zÄÖÜ]/g, '');
  return letters.length >= 5 && w === w.toUpperCase() && /[A-ZÄÖÜ]/.test(w);
}).length;

const groupWords = (words) => {
  const map = new Map();
  words.forEach((w) => {
    const key = (w || '').toLowerCase();
    if (!key) return;
    const ex = map.get(key);
    if (ex) ex.count += 1;
    else map.set(key, { word: w, count: 1 });
  });
  return Array.from(map.values());
};

/**
 * Rule-based, instant spam analysis of the email being composed.
 * @param {string} body    Email body (may contain HTML)
 * @param {string} subject Subject line
 * @param {{ isFollowUp?: boolean }} [options] Context flags. When isFollowUp is
 *   true the email is a reply within an existing thread, so a "Re:/Fwd:" prefix
 *   is legitimate and must NOT be flagged as a fake-reply spam trick.
 * @returns {{ level:'ok'|'warn'|'danger', score:number, spamWordCount:number,
 *   triggers:Array<{word:string,count:number}>, notes:Array<{type:string,text:string}>,
 *   source:'rules' }}
 */
export function analyzeSpamLocal(body, subject, options = {}) {
  const { isFollowUp = false } = options;
  const bodyPlain = stripHtml(body).replace(/\s+/g, ' ').trim();
  const subjectText = (subject || '').trim();

  const spamBody = scanPromotionalWords(bodyPlain);
  const spamSubject = scanPromotionalWords(subjectText);
  const spamWordCount = spamBody.count + spamSubject.count;
  const strength = spamBody.strength + spamSubject.strength;
  const triggers = groupWords([...spamBody.words, ...spamSubject.words]);

  const notes = [];
  const caps = countCapsWords(bodyPlain) + countCapsWords(subjectText);
  if (caps >= 1) notes.push({ type: 'caps', text: 'ALL-CAPS wording — reads like shouting to filters.' });
  if (PUNCT_RE.test(bodyPlain) || PUNCT_RE.test(subjectText)) notes.push({ type: 'punct', text: 'Stacked !!! / ??? punctuation is a classic promo signal.' });
  // A "Re:/Fwd:" prefix is only a spam trick on a first-contact/cold email. In a
  // follow-up (reply within an existing thread) it's legitimate, so we skip it.
  if (!isFollowUp && FAKE_REPLY_RE.test(subjectText)) notes.push({ type: 'reply', text: 'Fake "Re:/Fwd:" prefix — a spam trick and a CAN-SPAM issue.' });
  if (PHISHING_RE.test(bodyPlain) || PHISHING_RE.test(subjectText)) notes.push({ type: 'phish', text: 'Phishing-style phrasing (e.g. "verify your account").' });

  // Level — word/sentence driven, per the "≥2 spam words" rule.
  let level = 'ok';
  if (spamWordCount >= 4 || strength >= 6 || (spamWordCount >= 2 && notes.length >= 1)) level = 'danger';
  else if (spamWordCount >= 2 || notes.length >= 1) level = 'warn';
  if (notes.some((n) => n.type === 'phish')) level = 'danger';

  // Rough 1-10 score so the badge and tooltip can share language with the
  // template checker (10 = clean).
  let score = 10;
  score -= Math.min(6, strength * 1.4);
  score -= notes.length * 1.2;
  score = Math.max(1, Math.min(10, Math.round(score)));

  return { level, score, spamWordCount, triggers, notes, source: 'rules' };
}

// Normalize an AI cloud-function result into the same shape the badge renders.
export function normalizeAiResult(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const level = ['ok', 'warn', 'danger'].includes(raw.level) ? raw.level : 'ok';
  const triggers = Array.isArray(raw.triggers)
    ? raw.triggers.map((t) => (typeof t === 'string' ? { word: t, count: 1 } : { word: t.word || t.text || '', count: t.count || 1 })).filter((t) => t.word)
    : [];
  const notes = Array.isArray(raw.reasons)
    ? raw.reasons.filter(Boolean).map((r) => ({ type: 'ai', text: typeof r === 'string' ? r : (r.text || '') })).filter((n) => n.text)
    : [];
  const score = typeof raw.score === 'number' ? Math.max(1, Math.min(10, Math.round(raw.score))) : (level === 'danger' ? 3 : level === 'warn' ? 6 : 9);
  return { level, score, spamWordCount: triggers.reduce((a, t) => a + (t.count || 1), 0), triggers, notes, source: 'ai' };
}

const LEVEL_RANK = { ok: 0, warn: 1, danger: 2 };

/**
 * Combine the instant rule-based result with the AI verdict. The AI can only
 * make the verdict STRICTER, never more lenient — so if the hardcoded checker
 * flags spam wording (e.g. ≥2 promo words → "warn") the badge never drops to
 * green just because the model was optimistic. The AI still adds value by
 * escalating subtle/holistic spam the rules miss and enriching the explanation.
 */
export function mergeSpamResults(local, ai) {
  if (!ai) return local;
  if (!local) return ai;

  const localRank = LEVEL_RANK[local.level] ?? 0;
  const aiRank = LEVEL_RANK[ai.level] ?? 0;
  const level = aiRank >= localRank ? ai.level : local.level;
  const score = Math.min(
    typeof local.score === 'number' ? local.score : 10,
    typeof ai.score === 'number' ? ai.score : 10,
  );

  const seen = new Set();
  const triggers = [];
  [...(ai.triggers || []), ...(local.triggers || [])].forEach((t) => {
    const key = (t.word || '').toLowerCase();
    if (!key || seen.has(key)) return;
    seen.add(key);
    triggers.push(t);
  });

  const seenNotes = new Set();
  const notes = [];
  [...(ai.notes || []), ...(local.notes || [])].forEach((n) => {
    const key = (n.text || '').toLowerCase();
    if (!key || seenNotes.has(key)) return;
    seenNotes.add(key);
    notes.push(n);
  });

  return {
    level,
    score,
    spamWordCount: local.spamWordCount || 0,
    triggers: triggers.slice(0, 10),
    notes: notes.slice(0, 6),
    source: 'ai',
  };
}

export const spamLevelMeta = {
  ok: { color: '#22c55e', label: 'Looks clean — inbox-safe' },
  warn: { color: '#f59e0b', label: 'Might be flagged as spam' },
  danger: { color: '#ef4444', label: 'High spam risk' },
};
